import { useLocation, Navigate } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import { isAdmin } from "../utils/permissions";
import { useEffect, useState } from "react";

/**
 * RequireAdmin Component
 * Protects routes that should only be accessible to admin users (high_potit team)
 * Redirects non-admin users to unauthorized page
 */
const RequireAdmin = ({ children }) => {
  const { auth } = useAuth();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const employee = auth?.employee;

  // Check for token in localStorage directly as well (handles race condition after login)
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem("accessToken");
      const hasAuthToken = !!auth?.accessToken;

      // If we have a token in localStorage, give context time to sync
      if (token && !hasAuthToken) {
        // Token exists but context hasn't updated yet - wait a bit more
        setTimeout(() => setIsChecking(false), 150);
      } else {
        setIsChecking(false);
      }
    };

    // Initial delay to allow auth state to propagate
    const timer = setTimeout(checkAuth, 50);
    return () => clearTimeout(timer);
  }, [auth?.accessToken]);

  // Also check localStorage directly as fallback
  const localStorageToken = localStorage.getItem("accessToken");
  const hasValidToken = auth?.accessToken || localStorageToken;

  // Wait for auth check to complete
  if (isChecking) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-accent mx-auto mb-4"></div>
          <p className="text-sm text-gray-500 dark:text-light-500">Loading...</p>
        </div>
      </div>
    );
  }

  // If no token, redirect to login
  if (!hasValidToken) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If first login, redirect to change password
  if (auth?.isFirstLogin && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }

  // Check if user is admin
  const adminUser = isAdmin(employee);

  if (!adminUser) {
    // Redirect to unauthorized page
    return <Navigate to="/unauthorized" state={{ from: location }} replace />;
  }

  // User is admin, render the protected content
  return children;
};

export default RequireAdmin;
